import { TRPCClientError } from "@trpc/client";
import cuid from "cuid";
import { get, isString } from "lodash";
import toast from "react-hot-toast";

export function getFriendlyErrorMessage(error: unknown): string {
  let message = "Uh oh, something went wrong.";
  if (error instanceof TRPCClientError) {
    const code = isString(error.data?.code)
      ? String(get(error.data, "code"))
      : "unknown";

    // we'll try to generically handle all OpenAI errors
    if (code.startsWith("openai:")) {
      message =
        "We couldn’t complete your request because of the OpenAI issue(s) below.\n\n" +
        (error.data.serviceMessage ??
          `Status: ${error.data.serviceResponseStatus}`);
    } else {
      switch (code) {
        case "external:timeout":
          message = "A third-party service took too long to respond.";
          break;
        case "interfaces:domain-in-use":
          message =
            "That custom domain is already claimed by another Interfaces project.";
          break;
        case "vercel:invalid_domain":
          message = "That domain is invalid.";
          break;
        case "tables:limits:too_many_tables":
          message =
            "You have reached the maximum number of tables allowed in your account. Please upgrade your account to add more tables.";
          break;
        case "tables:limits:too_many_fields":
          message =
            "You have reached the maximum number of fields allowed in your account. Please upgrade your account to add more fields.";
          break;
        case "tables:limits:too_many_records":
          message =
            "You have reached the maximum number of records allowed in your account. Please upgrade your account to add more records.";
          break;
        case "tables:permissions:errors":
          message =
            "This page is connected to a Table you don't have access to.";
          break;
        case "block:chatbot:directive-too-long":
          message = "Chatbot directive is too long.";
          break;
        case "interfaces:collaborators:cannot-be-creator":
          message = "Project creator cannot be a collaborator.";
          break;
        case "interfaces:collaborators:too-many-collaborators":
          message = "Project has too many collaborators.";
          break;
        case "interfaces:collaborators:collaborator-already-exists":
          message = "Project already has this collaborator.";
          break;
        case "interfaces:collaborators:account-member-does-not-exist":
          message = "Account member does not exist.";
          break;
        case "interfaces:collaborators:collaborator-does-not-exist":
          message = "Project does not have this collaborator.";
          break;
        case "interfaces:collaborators:cannot-delete-creator":
          message = "Cannot remove the project owner.";
          break;
      }
    }
  }
  return message + `\n\n${extractErrorId(error)}`;
}

export function toastFriendlyErrorMessage(
  error: unknown,
  errorMessageOverride?: string
) {
  const message = errorMessageOverride ?? getFriendlyErrorMessage(error);
  return toast.error(message, {
    id: get(error, "data.path") ?? get(error, "id"),
    duration: Infinity,
  });
}

function extractErrorId(error: unknown) {
  return `\`«${get(error, "id", cuid.slug())}»\``;
}
